// client/src/routes/_auth/select-client.tsx — „Pentru cine lucrezi?" · AUTH-006 (D-1707).
//
// Under `_auth` for the reason `/select-contract` is: it is a page with nothing on it but the
// question, and `AuthLayout` is the layout that has no navigation to leak.
//
// The guard answers three states before the component renders, so the page is only ever shown
// when there is a real choice to make:
//   • no session → `/login`, with the deep link forwarded;
//   • no clients → wherever this account's role lives. An owner acting as themselves, a portal
//     tenant and a platform admin all reach `/app`'s own guard, which knows where to send them;
//   • a client already active, or exactly ONE to choose → straight on, choosing it if needed.
//     One client is not a choice, and this is not „no last-used memory" bending (D-1698):
//     nothing is remembered between sessions.

import { createFileRoute, redirect } from '@tanstack/react-router';

import { authApi } from '@/api/auth';
import { DEFAULT_LANDING, forwardRedirect, redirectSearchSchema, safeRedirect } from '@/features/auth/redirect';
import { SelectClient } from '@/features/auth/SelectClient';
import { establishSession, ensureSession } from '@/session';

export const Route = createFileRoute('/_auth/select-client')({
  validateSearch: redirectSearchSchema,
  beforeLoad: async ({ search }) => {
    const session = await ensureSession();
    if (!session) throw redirect({ to: '/login', search: forwardRedirect(search) });

    if (session.clients.length === 0 || session.activeClientId !== null) {
      throw redirect({ href: safeRedirect(search.redirect ?? DEFAULT_LANDING) });
    }

    if (session.clients.length === 1) {
      // Chosen here rather than on the page, so the person never sees a list of one. A failure
      // falls through to the page, which offers the row and a real error if it is pressed —
      // better than a redirect loop between this guard and `/app`'s.
      const only = session.clients[0];

      if (only !== undefined) {
        const issued = await authApi.switchClient(only.id).catch(() => undefined);

        if (issued !== undefined) {
          await establishSession(issued);
          throw redirect({ href: safeRedirect(search.redirect ?? DEFAULT_LANDING) });
        }
      }
    }
  },
  component: SelectClientScreen,
});

function SelectClientScreen() {
  const search = Route.useSearch();
  return <SelectClient search={search} />;
}
